import {
  Button,
  Container,
  Paper,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  Switch,
  FormHelperText,
  Box,
  Chip,
  MenuItem,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { IMAGE_URL } from "network";
import { useState } from "react";
import { uploadImage } from "services/imageUpload";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useGetSingleBlogQuery,
  useAddBlogMutation,
  useUpdateBlogMutation,
} from "slices/cms/blogSlice";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import Select from "react-select";
import { useGetProductsQuery } from "slices/beautyApp/productSlice";
import { useGetProductsQuery as productsFc } from "slices/eCommerce/shopProducts";

const extractServicesFromItems = (items = []) =>
  items.filter((i) => i.itemType === "products").map((i) => i.itemRef);

const extractProductsFromItems = (items = []) =>
  items.filter((i) => i.itemType === "shop_product").map((i) => i.itemRef);
// ✅ Validation Schema
const BlogSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  authorName: Yup.string().required("Author name is required"),
  publishDate: Yup.date().required("Publish date is required"),
  status: Yup.boolean(),
  metaTitle: Yup.string().required("Meta title is required"),
  metaDescription: Yup.string().required("Meta description is required"),
  metaKeywords: Yup.array()
    .min(1, "At least one keyword is required")
    .of(Yup.string().required()),
  authorImage: Yup.string().required("Author image is required"),
  canonicalUrl: Yup.string()
    .url("Must be a valid URL")
    .required("Canonical URL is required"),
  robots: Yup.string().required("Robots tag is required"),
  faqSchema: Yup.string().test(
    "is-valid-json",
    "FAQ Schema must be valid JSON",
    (value) => {
      if (!value) return true;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    }
  ),
});

function AddBlog() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);
  const isMediaCoverage = location.pathname.includes("media-coverage");
  const contentType = isMediaCoverage ? "media-coverage" : "blog";
  const { data: initialData, isSuccess } = useGetSingleBlogQuery(id, {
    skip: !isEdit,
  });

  const [addBlog] = useAddBlogMutation();
  const [updateBlog] = useUpdateBlogMutation();
  const [uploading, setUploading] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");

  const { data: services } = useGetProductsQuery({});
  const { data: products } = productsFc({});

  // ✅ Map API to Select Options
  const serviceOptions =
    services?.data?.map((s) => ({ value: s._id, label: s.name })) || [];
  const productOptions =
    products?.data?.data?.map((p) => ({ value: p._id, label: p.name })) || [];

  // ✅ Map selected IDs to Select values
  const mapToSelectValues = (ids, options) =>
    options.filter((opt) => ids?.includes(opt.value));

  // ✅ Formik Setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      type: contentType,
      title: isEdit && isSuccess ? initialData?.title || "" : "",
      description: isEdit && isSuccess ? initialData?.description || "" : "",
      authorName: isEdit && isSuccess ? initialData?.authorName || "" : "",
      publishDate:
        isEdit && isSuccess
          ? initialData?.publishDate
            ? initialData.publishDate.split("T")[0]
            : ""
          : "",
      status: isEdit && isSuccess ? initialData?.status || false : false,
      metaTitle: isEdit && isSuccess ? initialData?.metaTitle || "" : "",
      metaDescription:
        isEdit && isSuccess ? initialData?.metaDescription || "" : "",
      metaKeywords:
        isEdit && isSuccess
          ? initialData?.metaKeywords
            ? initialData.metaKeywords.split(",")
            : []
          : [],
      authorImage: isEdit && isSuccess ? initialData?.authorImage || "" : "",
      canonicalUrl: isEdit && isSuccess ? initialData?.canonicalUrl || "" : "",
      robots:
        isEdit && isSuccess
          ? initialData?.robots || "index, follow"
          : "index, follow",
      faqSchema: isEdit && isSuccess ? initialData?.faqSchema || "" : "",

      // keep existing fields for UI
      services:
        isEdit && isSuccess ? extractServicesFromItems(initialData?.items) : [],
      products:
        isEdit && isSuccess ? extractProductsFromItems(initialData?.items) : [],
    },

    validationSchema: BlogSchema,

    onSubmit: async (values, { resetForm }) => {
      // ✅ build combined items array
      const items = [
        ...(values.services || []).map((id) => ({
          itemType: "products",
          itemRef: id,
        })),
        ...(values.products || []).map((id) => ({
          itemType: "shop_product",
          itemRef: id,
        })),
      ];

      const payload = {
        ...values,
        metaKeywords: values.metaKeywords.join(","), // array → string
        items, // ✅ send to backend
      };

      // optional: remove unused fields for backend
      delete payload.services;
      delete payload.products;

      try {
        if (isEdit) {
          await updateBlog({ id, body: payload }).unwrap();
        } else {
          await addBlog(payload).unwrap();
        }

        resetForm();

        if (contentType === "media-coverage") {
          navigate("/cms/media-coverage", { state: { refresh: true } });
        } else {
          navigate("/cms/blogs", { state: { refresh: true } });
        }
      } catch (err) {
        console.error(err);
        alert("Something went wrong!");
      }
    },
  });

  // ✅ Keywords
  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (kw && !formik.values.metaKeywords.includes(kw)) {
      formik.setFieldValue("metaKeywords", [...formik.values.metaKeywords, kw]);
      setKeywordInput("");
    }
  };
  const removeKeyword = (kw) => {
    formik.setFieldValue(
      "metaKeywords",
      formik.values.metaKeywords.filter((k) => k !== kw)
    );
  };

  // ✅ Image Upload Handler
  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await uploadImage(file);
      formik.setFieldValue(fieldName, response || "");
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" mb={3}>
            {contentType === "media-coverage"
              ? id
                ? "Edit Media"
                : "Add Media"
              : id
              ? "Edit Blog"
              : "Add Blog"}
          </Typography>

          <form
            onSubmit={formik.handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            {/* Title */}
            <TextField
              label="Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
              fullWidth
            />

            {/* Description */}
            <FormLabel>Description</FormLabel>
            <CKEditor
              editor={ClassicEditor}
              data={formik.values.description}
              config={{
                toolbar: [
                  "heading",
                  "|",
                  "bold",
                  "italic",
                  "underline",
                  "|",
                  "bulletedList",
                  "numberedList",
                  "|",
                  "insertTable",
                  "link",
                  "imageUpload",
                  "|",
                  "undo",
                  "redo",
                ],
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                  ],
                },
              }}
              onChange={(event, editor) => {
                formik.setFieldValue("description", editor.getData());
              }}
            />
            {formik.touched.description && formik.errors.description && (
              <FormHelperText error>{formik.errors.description}</FormHelperText>
            )}

            {/* Author Name */}
            <TextField
              label="Author Name"
              name="authorName"
              value={formik.values.authorName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.authorName && Boolean(formik.errors.authorName)
              }
              helperText={formik.touched.authorName && formik.errors.authorName}
              fullWidth
            />

            {/* Publish Date */}
            <TextField
              label="Publish Date"
              name="publishDate"
              type="date"
              value={formik.values.publishDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.publishDate && Boolean(formik.errors.publishDate)
              }
              helperText={
                formik.touched.publishDate && formik.errors.publishDate
              }
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            {/* Select Services */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tag Services
              </Typography>
              <Select
                isMulti
                name="services"
                options={serviceOptions}
                value={mapToSelectValues(
                  formik.values.services,
                  serviceOptions
                )}
                onChange={(selected) =>
                  formik.setFieldValue(
                    "services",
                    selected ? selected.map((o) => o.value) : []
                  )
                }
              />
            </Box>

            {/* Select Products */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tag Products
              </Typography>
              <Select
                isMulti
                name="products"
                options={productOptions}
                value={mapToSelectValues(
                  formik.values.products,
                  productOptions
                )}
                onChange={(selected) =>
                  formik.setFieldValue(
                    "products",
                    selected ? selected.map((o) => o.value) : []
                  )
                }
              />
            </Box>

            {/* Status */}
            <FormControl>
              <FormLabel>Status</FormLabel>
              <Switch
                name="status"
                checked={formik.values.status}
                onChange={(e) =>
                  formik.setFieldValue("status", e.target.checked)
                }
              />
            </FormControl>

            {/* SEO Fields */}
            <TextField
              label="Meta Title"
              name="metaTitle"
              value={formik.values.metaTitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.metaTitle && Boolean(formik.errors.metaTitle)
              }
              helperText={formik.touched.metaTitle && formik.errors.metaTitle}
              fullWidth
            />
            <TextField
              label="Meta Description"
              name="metaDescription"
              value={formik.values.metaDescription}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.metaDescription &&
                Boolean(formik.errors.metaDescription)
              }
              helperText={
                formik.touched.metaDescription && formik.errors.metaDescription
              }
              fullWidth
            />

            {/* Keywords */}
            <Typography variant="h6" gutterBottom>
              Keywords
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                label="Add Keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
                fullWidth
              />
              <Button onClick={addKeyword} variant="contained">
                Add
              </Button>
            </Box>
            <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
              {formik.values.metaKeywords.map((k) => (
                <Chip key={k} label={k} onDelete={() => removeKeyword(k)} />
              ))}
            </Box>
            {formik.touched.metaKeywords && formik.errors.metaKeywords && (
              <FormHelperText error>
                {formik.errors.metaKeywords}
              </FormHelperText>
            )}

            {/* Main Image */}
            <FormControl>
              <FormLabel>Image</FormLabel>
              <input
                type="file"
                onChange={(e) => handleImageUpload(e, "authorImage")}
              />
              {formik.errors.authorImage && (
                <FormHelperText error>
                  {formik.errors.authorImage}
                </FormHelperText>
              )}
              {formik.values.authorImage && (
                <img
                  crossOrigin="anonymous"
                  src={`${IMAGE_URL}${formik.values.authorImage}`}
                  alt="Main"
                  style={{ marginTop: "1rem", maxWidth: "100px" }}
                />
              )}
            </FormControl>

            <TextField
              label="Canonical URL"
              name="canonicalUrl"
              value={formik.values.canonicalUrl}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.canonicalUrl &&
                Boolean(formik.errors.canonicalUrl)
              }
              helperText={
                formik.touched.canonicalUrl && formik.errors.canonicalUrl
              }
              fullWidth
            />

            <TextField
              select
              label="Robots Tag"
              name="robots"
              value={formik.values.robots}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.robots && Boolean(formik.errors.robots)}
              helperText={formik.touched.robots && formik.errors.robots}
              fullWidth
            >
              <MenuItem value="index, follow">Index</MenuItem>
              <MenuItem value="noindex, follow">No Index</MenuItem>
            </TextField>

            <TextField
              label="FAQ Schema (JSON only)"
              name="faqSchema"
              value={formik.values.faqSchema}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.faqSchema && Boolean(formik.errors.faqSchema)
              }
              helperText={
                formik.touched.faqSchema && formik.errors.faqSchema
                  ? formik.errors.faqSchema
                  : "Paste valid JSON only. Do not include <script> tags."
              }
              multiline
              rows={8}
              fullWidth
            />

            {/* Submit */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={uploading}
            >
              {id ? "Update Blog" : "Add Blog"}
            </Button>
          </form>
        </Paper>
      </Container>
    </DashboardLayout>
  );
}

export default AddBlog;
